import {
  Alert,
  IconButton,
  Paper,
  Slide,
  Tooltip,
  Button,
  Typography,
} from "@mui/material";
import { Box, Grid } from "@mui/system";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import HistoryIcon from "@mui/icons-material/History";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import DailyMonthlyPerformanceTabs from "./DailyMonthlyPerformance/DailyMonthlyPerformanceTabs";
import "./BrandConfigurationOverview.css";

import { useEffect, useState } from "react";
import LastRuns from "./RunModals/LastRuns";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import BrandConfigOverviewSkeleton from "../BrandSkeletons/BrandConfigOverviewSkeleton";
import { useCopyToClipboard } from "../brandPropertiesHook/useCopyToClipboard";
import { selectUserToken } from "../../../../../features/userSlice";
import { BrandsEndPointLogic } from "../../../../../logic/Partners/DataIntegrations/BrandsEndPointLogic";
import {
  setBrandConfigOverviewLoader,
  setFetchedTabs,
  setIsFetchedBrandConfigOverviewData,
  setIsSuccess,
  setModalOpen,
  setProcessFailure,
} from "../../../../../features/brandConfigurationReportSlice";
import {
  formatDateTime,
  QUERY_IDS,
  REPORT_QUERY_ID_MAP,
} from "../utilitiesOfBrandConfig/helperOfBrandConfig";
import RunBrandData from "./RunModals/RunBrandData/RunBrandData";
import { CheckPermissions } from "../../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import StatusChip from "../utilitiesOfBrandConfig/StatusChip";
import { useWebsiteSharedUid } from "../../../../../customHooks/useWebsiteSharedUid";
//import ObjectsSection from "./ObjectsSection/ObjectsSection";
import AdvisorAndUnifier from "./AdvisorAndUnifier/AdvisorAndUnifier";

// Header card typography/spacing, mapped 1:1 from the Brand Configuration
// Overview Figma (Brands file, node 2812-73297). Kept as constants because
// each one is used by three sibling rows across the two cards — Account Name /
// Affiliate System / Last Run — which must stay visually identical.
const CARD_LABEL_SX = {
  fontSize: "12px", // components/input-label
  lineHeight: "12px",
  letterSpacing: "0.15px",
  color: "rgba(0, 0, 0, 0.6)", // text/secondary
};

const CARD_VALUE_SX = {
  fontSize: "14px", // typography/body2
  lineHeight: "143%",
  letterSpacing: "0.17px",
  color: "rgba(0, 0, 0, 0.87)", // text/primary
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

// caption — the three underlined link rows (Copy Keeper ID, See Last 10 Runs,
// Manual Config). Colour is intentionally left to each call site, which
// already varies it by enabled/disabled state.
const CARD_CAPTION_SX = {
  fontSize: "10px",
  lineHeight: "166%",
  letterSpacing: "0.4px",
};

// Shared <CardContent> box: 8px padding, 8px radius, 1px divider border and a
// 4px gap between stacked rows. Only the width differs between the two cards
// (324px / 349px), which with the 16px row gap adds up to the 689px header.
const HEADER_CARD_SX = {
  p: 1,
  boxShadow: "none",
  borderRadius: "8px",
  border: "1px solid rgba(0, 0, 0, 0.12)", // divider
};

// Keywords Table date
const tableHeadings = {
  process_name: "Process Name",
  range: "Range",
  status: "Status",
  step: "Step",
  started_at: "Run Start",
  ended_at: "Run End",
};

const BrandConfigurationOverview = () => {
  const dispatch = useDispatch();

  const [searchParams, setSearchParams] = useSearchParams("");
  const [lastRunLoader, setLastRunLoader] = useState(false);
  const brandIdFromURL = searchParams.get("brand_id") || ""; // Get brand_id from URL query parameters
  const reportFromURL = searchParams.get("report") || "daily_user"; // Get report type from URL query parameters (default to "daily_user" if not provided)
  const start_date = searchParams.get("start_date") || null; // Get start_date from URL query parameters
  const end_date = searchParams.get("end_date") || null;
  const mainBrandStatus = searchParams.get("mainBrandStatus") || null;

  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const [openLastRunModal, setOpenLastRunModal] = useState(false);

  const { isModalOpen, fetcherConfigData } = useSelector(
    (state: any) => state.brandConfigurationReport,
  );
  const { userData } = useSelector((state: any) => state.user);
  const type = "site";

  const QUERY_ID = "5652708983832576";
  const INDUSTRY_QUERY_ID = "5171209863954432";

  // Get 1st website_shared_uid from website_permissions based on current requirements
  const website_shared_uid = useWebsiteSharedUid();

  const { copied, copy } = useCopyToClipboard(); // Custom hook for clipboard copy

  const isFetchedBrandConfigOverviewData = useSelector(
    (state: any) =>
      state.brandConfigurationReport.isFetchedBrandConfigOverviewData,
  );
  const { isSuccess, isProcessFailed, processFailureMessage, resWorkflowName } =
    useSelector((state: any) => state.brandConfigurationReport);

  // fetch brand account overview info
  const brandAccountOverviewInfo = useSelector(
    (state: any) => state.brandConfigurationReport.brandAccountOverviewInfo,
  );
  // fetch last run data
  const brandOverviewLastRunData = useSelector(
    (state: any) => state.brandConfigurationReport.brandOverviewLastRunData,
  );

  const handleLastRunModal = () => {
    setOpenLastRunModal(true);
  };
  const handleCloseModal = () => {
    setOpenLastRunModal(false);
  };

  // Fetch the overview data when the component mounts
  useEffect(() => {
    const fetchBrandConfigOverviewData = async (
      brandIdFromURL: string | any,
      reportFromURL: string | any,
    ) => {
      if (!brandIdFromURL?.trim() || !reportFromURL || !website_shared_uid)
        return;

      const hasUrlDates = !!start_date && !!end_date;

      dispatch(setBrandConfigOverviewLoader(true));
      dispatch(setIsFetchedBrandConfigOverviewData(false));
      try {
        await Promise.all([
          BrandsEndPointLogic.getBrandAccountOverviewInfo(
            oauthTokenHeader,
            QUERY_IDS.BRAND_ACCOUNT_OVERVIEW_INFO,
            website_shared_uid,
            type,
            brandIdFromURL,
          ),

          BrandsEndPointLogic.getOverviewPerformance(
            oauthTokenHeader,
            reportFromURL,
            REPORT_QUERY_ID_MAP[reportFromURL] ??
              QUERY_IDS.DAILY_USER_PERFORMANCE,
            website_shared_uid,
            type,
            brandIdFromURL,
            hasUrlDates ? start_date : null, // ← null on fresh load
            hasUrlDates ? end_date : null, // ← null on fresh load
            hasUrlDates ? 0 : 10,
          ),

          BrandsEndPointLogic.getBrandOverviewAdvisorSettings(
            oauthTokenHeader,
            brandIdFromURL,
            reportFromURL,
          ),
          BrandsEndPointLogic.getObjectsData(
            oauthTokenHeader,
            QUERY_IDS.OBJECTS_SECTION,
            website_shared_uid,
            type,
            brandIdFromURL,
          ),
          BrandsEndPointLogic.getAllConfigForFetcher(
            oauthTokenHeader,
            QUERY_ID,
            website_shared_uid,
          ),
          BrandsEndPointLogic.getBrandsForIndustry(
            oauthTokenHeader,
            INDUSTRY_QUERY_ID,
            website_shared_uid,
          ),
        ]);

        const response =
          await BrandsEndPointLogic.getBrandOverviewUnifierSettings(
            oauthTokenHeader,
            brandIdFromURL,
            reportFromURL,
          );

        // fetch unique raw table ids from Unifier settings
        const rawTableIds =
          Array.isArray(response) && response.length > 0
            ? [
                ...new Set(
                  response.map((item: any) => Number(item.raw_table_id)),
                ),
              ]
            : [];

        const parsedBrandId = parseInt(brandIdFromURL, 10);
        const parsedRawTableIds = rawTableIds
          .map((id: any) => parseInt(id, 10))
          .filter((id) => !isNaN(id)); // drop any non-numeric ids
        // Guard: skip fetcher call if either value is unusable
        if (!isNaN(parsedBrandId) && parsedRawTableIds.length > 0) {
          const requestBodyForFetcher = {
            brand_id: parsedBrandId,
            raw_table_id: parsedRawTableIds,
          };
          await BrandsEndPointLogic.getBrandOverviewFetcherSettings(
            oauthTokenHeader,
            requestBodyForFetcher,
            reportFromURL,
          );
        }

        // //  Mark the initial tab as fetched so the tab effect skips it
        dispatch(setFetchedTabs(reportFromURL as any));
      } catch (error) {
        console.error(
          "Error fetching brand configuration overview data:",
          error,
        );
      } finally {
        dispatch(setBrandConfigOverviewLoader(false));
        dispatch(setIsFetchedBrandConfigOverviewData(true));
      }
      // eslint-disable-next-line
    };

    // Fetch last run data (Separately called as ts take too much time to load)
    const fetchLastRunDataForAllTabs = async (
      brandIdFromURL: string | any,
      reportFromURL: string | any,
    ) => {
      if (!brandIdFromURL?.trim() || !reportFromURL) return;
      setLastRunLoader(true);
      try {
        await BrandsEndPointLogic.getBrandOverviewLastRuns(
          oauthTokenHeader,
          brandIdFromURL,
        );
      } catch (error) {
        console.error("Error fetching last run data for all tabs:", error);
      } finally {
        setLastRunLoader(false);
      }
    };

    fetchBrandConfigOverviewData(brandIdFromURL, reportFromURL);
    // Fetch last run data (seperately called as it takes too much time to load)
    fetchLastRunDataForAllTabs(brandIdFromURL, reportFromURL);

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    dispatch(setIsSuccess(false));
    dispatch(setProcessFailure({ isFailed: false, message: "" }));
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    dispatch(setIsSuccess(false));
    dispatch(setProcessFailure({ isFailed: false, message: "" }));
  };

  const handleProcessLogsNavigation = () => {
    const today = new Date().toISOString().split("T")[0];
    const query = new URLSearchParams({
      start_date: today,
      end_date: today,
      workflow_name: resWorkflowName,
      page: "0",
    });
    window.open(`/data_collection/data_logs?${query.toString()}`, "_blank");
  };

  const brandId = brandAccountOverviewInfo[0]?.brand_id;
  const hasViewMonitoringPermission = CheckPermissions(
    userData?.data?.products?.data_collection,
    "monitoring",
    "view_monitoring",
  );
  const hasViewWorkflowPermission = CheckPermissions(
    userData?.data?.products?.data_collection,
    "monitoring",
    "view_workflows",
  );
  const hasPermissionForClick: any =
    (hasViewMonitoringPermission && hasViewWorkflowPermission) || false;
  const row_output_table_data = Array.from(
    new Map(
      (fetcherConfigData?.query_output || [])
        .filter((item: any) => item.brand_id === brandId)
        .map((item: any) => [
          item.raw_output_table_name, // unique key (or config_id if safer)
          {
            config_name: item.config_name,
            table_id: item.raw_output_table_name,
          },
        ]),
    ).values(),
  );

  // Get the latest record based on ended_at
  const latestRecordByEndedAt = [...brandOverviewLastRunData]
    .filter((record) => record.ended_at != null) // ← exclude null/undefined
    .sort(
      (a, b) => new Date(b.ended_at).getTime() - new Date(a.ended_at).getTime(),
    )[0];

  // Extract the latest status and last run by ended_at
  const latestStatusByEndedAt = latestRecordByEndedAt?.status ?? "";
  const lastRunByEndedAt = latestRecordByEndedAt?.ended_at ?? "";

  // Get the latest record based on started_at
  const latestRecordByStaredAt = [...brandOverviewLastRunData]
    .filter((record) => record.started_at != null) // ← exclude null/undefined
    .sort(
      (a, b) =>
        new Date(b.started_at).getTime() - new Date(a.started_at).getTime(),
    )[0];

  // Extract the latest status and last run by started_at
  const latestRunStatusByStartedAt = latestRecordByStaredAt?.status ?? "";
  const lastRunByStartedAt = latestRecordByStaredAt?.started_at ?? "";

  //new changes (https://optival.atlassian.net/browse/GR-21070)
  // Pick which status/date to show based on the brand's main status
  const isMainBrandStatusRunning = mainBrandStatus === "Running";
  const displayStatus = isMainBrandStatusRunning
    ? latestRunStatusByStartedAt
    : latestStatusByEndedAt;
  const displayLastRun = isMainBrandStatusRunning
    ? lastRunByStartedAt
    : lastRunByEndedAt;

  // Remove the rawTableId, fieldMappingId, metric and affiliateTerm params if they exist
  useEffect(() => {
    const paramsToRemove = [
      "rawTableId",
      "fieldMappingId",
      "metric",
      "affiliateTerm",
    ];
    const hasAny = paramsToRemove.some((key) => searchParams.has(key));

    if (hasAny) {
      const newParams = new URLSearchParams(searchParams);
      paramsToRemove.forEach((key) => newParams.delete(key));
      setSearchParams(newParams, { replace: true }); // replace: true avoids polluting browser history
    }
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <Slide
        direction="left"
        in={isSuccess}
        mountOnEnter
        unmountOnExit
        timeout={200}
      >
        <div>
          <Alert
            severity="success"
            onClose={handleClose}
            sx={{
              mb: 2,
              border: "1px solid rgba(0, 128, 0, 0.3)",
              backgroundColor: "rgba(240, 255, 240, 0.9)",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: "17px",
                fontWeight: "500",
                marginBottom: "4px",
              }}
            >
              The process has been launched successfully!
            </Typography>
            {hasPermissionForClick && (
              <>
                To monitor the workflow results{" "}
                <span
                  onClick={handleProcessLogsNavigation}
                  style={{
                    textDecoration: "underline",
                    color: "#9C27B0",
                    cursor: "pointer",
                  }}
                  data-tracker="790"
                >
                  CLICK HERE
                </span>{" "}
                (It may take a few minutes for the logs to appear in the Process
                Log Table.)
              </>
            )}
          </Alert>
        </div>
      </Slide>
      <Slide
        direction="left"
        in={isProcessFailed}
        mountOnEnter
        unmountOnExit
        timeout={200}
      >
        <div>
          <Alert
            severity="error"
            onClose={handleClose}
            sx={{
              mb: 2,
              border: "1px solid rgba(211, 47, 47, 0.35)",
              backgroundColor: "rgba(255, 235, 238, 0.9)",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: "17px",
                fontWeight: "500",
                marginBottom: "4px",
              }}
            >
              The process request had failed
            </Typography>
            {processFailureMessage || "Unknown error occurred"}
          </Alert>
        </div>
      </Slide>
      {isFetchedBrandConfigOverviewData ? (
        <>
          <Paper>
            <Grid container>
              <Grid size={5} p={2}>
                <Box display="flex" alignItems="center" gap={2}>
                  {brandAccountOverviewInfo[0]?.brand_name && (
                    <Typography sx={{}} variant="h5">
                      {brandAccountOverviewInfo[0]?.brand_name}
                    </Typography>
                  )}

                  {brandAccountOverviewInfo[0]?.brand_status && (
                    <Typography sx={{}} variant="h5">
                      <StatusChip
                        status={brandAccountOverviewInfo[0]?.brand_status}
                      />
                    </Typography>
                  )}
                </Box>
                <Box mt={2} display="flex" alignItems="center" gap={2}>
                  {brandAccountOverviewInfo[0]?.company_name && (
                    <Typography variant="body2">
                      Company:{" "}
                      <b>{brandAccountOverviewInfo[0]?.company_name}</b>
                    </Typography>
                  )}
                  {brandAccountOverviewInfo[0]?.brand_id && (
                    <Typography variant="body2">
                      Brand ID: <b>{brandAccountOverviewInfo[0]?.brand_id}</b>
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid
                size={7}
                pt={1}
                pb={2}
                px={2}
                display={"flex"}
                gap={2}
                justifyContent={"flex-end"}
                sx={{ pl: 4 }}
              >
                <Paper sx={{ ...HEADER_CARD_SX, width: "324px" }}>
                  <Grid container spacing={2}>
                    {/* Left column: label + status chip, then account name */}
                    <Grid size={7} minWidth={0}>
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={2}
                        mb={0.5}
                        minHeight="18px"
                      >
                        <Typography variant="body2" sx={CARD_LABEL_SX}>
                          Account Name
                        </Typography>
                        <StatusChip
                          size="small"
                          status={brandAccountOverviewInfo[0]?.account_status}
                        />
                      </Box>

                      <Tooltip
                        title={
                          brandAccountOverviewInfo[0]?.account_username ?? ""
                        }
                        placement="top"
                        arrow
                      >
                        <Typography sx={CARD_VALUE_SX}>
                          {brandAccountOverviewInfo[0]?.account_username ?? ""}
                        </Typography>
                        
                      </Tooltip>
                    </Grid>

                    {/* Right column: label, affiliate system + login link, copy keeper id */}
                    <Grid size={5} minWidth={0}>
                      <Typography
                        variant="body2"
                        sx={CARD_LABEL_SX}
                        mb={0.5}
                        minHeight="18px"
                        display="flex"
                        alignItems="center"
                      >
                        Affiliate System
                      </Typography>

                      <Box display="flex" alignItems="center" gap={1}>
                        <Tooltip
                          title={
                            brandAccountOverviewInfo[0]?.affiliate_system ?? ""
                          }
                          placement="top"
                          arrow
                        >
                          <Typography sx={CARD_VALUE_SX}>
                            {brandAccountOverviewInfo[0]?.affiliate_system ?? ""}
                          </Typography>
                        </Tooltip>
                        {brandAccountOverviewInfo[0]?.login_url && (
                          <RouterLink
                            style={{ color: "#9C27B0", display: "flex" }}
                            to={brandAccountOverviewInfo[0]?.login_url}
                            target="_blank"
                          >
                            <IconButton
                              data-tracker="785"
                              size="small"
                              color="secondary"
                            >
                              <OpenInNewIcon fontSize="inherit" />
                            </IconButton>
                          </RouterLink>
                        )}
                      </Box>

                      {brandAccountOverviewInfo[0]?.keeper_id && (
                        <Tooltip
                          title={copied ? "Copied!" : "Copy"}
                          placement="top"
                          arrow
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              ...CARD_CAPTION_SX,
                              textTransform: "uppercase",
                              color: "#7B1FA2",
                              display: "inline-flex",
                              alignItems: "center",
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              copy(brandAccountOverviewInfo[0]?.keeper_id)
                            }
                          >
                            <IconButton
                              data-tracker="786"
                              size="small"
                              color={copied ? "success" : "inherit"}
                              sx={{ mr: 0.5, p: 0 }}
                            >
                              {copied ? (
                                <CheckCircleOutlinedIcon fontSize="inherit" />
                              ) : (
                                <ContentCopyIcon fontSize="inherit" />
                              )}
                            </IconButton>
                            <Box
                              component="span"
                              sx={{ textDecoration: "underline" }}
                            >
                              Copy Keeper ID
                            </Box>
                          </Typography>
                        </Tooltip>
                      )}
                    </Grid>
                  </Grid>
                </Paper>

                <Paper sx={{ ...HEADER_CARD_SX, width: "349px" }}>
                  {/* Top row: Last Run label + status chip and date, Run Brand */}
                  <Box
                    display={"flex"}
                    justifyContent={"space-between"}
                    alignItems={"flex-start"}
                    gap={2}
                    sx={{ mb: 0.5 }}
                  >
                    <Box minWidth={0}>
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={2}
                        mb={0.5}
                        minHeight="18px"
                      >
                        <Typography variant="body2" sx={CARD_LABEL_SX}>
                          Last Run
                        </Typography>
                        <StatusChip size="small" status={displayStatus} />
                      </Box>
                      {displayLastRun ? (
                        <Typography sx={CARD_VALUE_SX}>
                          {formatDateTime(displayLastRun)}
                        </Typography>
                      ) : (
                        <span>&nbsp;</span>
                      )}
                    </Box>

                    <Button
                      data-tracker="787"
                      variant="contained"
                      color="secondary"
                      endIcon={<PlayCircleFilledIcon />}
                      sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                      onClick={() =>
                        dispatch(
                          setModalOpen({
                            isOpen: true,
                            data: {
                              brandName:
                                brandAccountOverviewInfo[0]?.brand_name,
                              brandId: brandAccountOverviewInfo[0]?.brand_id,
                              row_output_table_data,
                            },
                          }),
                        )
                      }
                    >
                      Run Brand
                    </Button>

                    <RunBrandData
                      open={isModalOpen}
                      //handleClose={handleCloseRunBrandModal}
                    />
                  </Box>

                  {/* Bottom row: See Last 10 Runs + Manual Config */}
                  <Box
                    display={"flex"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                    gap={2}
                  >
                    {lastRunLoader ? (
                      <Typography
                        variant="body2"
                        sx={{
                          ...CARD_CAPTION_SX,
                          color: "#00000099",
                          display: "inline-flex",
                          alignItems: "center",
                        }}
                      >
                        <IconButton
                          size="small"
                          disabled
                          sx={{ mr: 0.5, p: 0 }}
                        >
                          <AccessTimeIcon
                            fontSize="inherit"
                            sx={{
                              animation: "spinFade 1.4s ease-in-out infinite",
                              "@keyframes spinFade": {
                                "0%, 100%": {
                                  opacity: 0.35,
                                  transform: "translateY(2px) rotate(0deg)",
                                },
                                "50%": {
                                  opacity: 1,
                                  transform: "translateY(-2px) rotate(180deg)",
                                },
                                "100%": {
                                  transform: "translateY(2px) rotate(360deg)",
                                },
                              },
                            }}
                          />
                        </IconButton>
                        Last Run
                      </Typography>
                    ) : (
                      <Typography
                        variant="body2"
                        onClick={
                          Array.isArray(brandOverviewLastRunData) &&
                          brandOverviewLastRunData?.length > 0
                            ? handleLastRunModal
                            : undefined
                        }
                        sx={{
                          ...CARD_CAPTION_SX,
                          color:
                            brandOverviewLastRunData?.length === 0
                              ? "#00000099"
                              : "secondary.main",
                          display: "inline-flex",
                          alignItems: "center",
                          cursor:
                            Array.isArray(brandOverviewLastRunData) &&
                            brandOverviewLastRunData?.length > 0
                              ? "pointer"
                              : "default",
                        }}
                      >
                        <IconButton
                          data-tracker="789"
                          aria-label="see last 10 runs"
                          size="small"
                          color="secondary"
                          sx={{ mr: 0.5, p: 0 }}
                          disabled={
                            !Array.isArray(brandOverviewLastRunData) ||
                            brandOverviewLastRunData?.length === 0
                          } // Disable if there's no last run data
                          //onClick={handleLastRunModal}
                        >
                          <HistoryIcon fontSize="inherit" />
                        </IconButton>
                        <Box
                          component="span"
                          sx={{
                            textDecoration:
                              brandOverviewLastRunData?.length === 0
                                ? "none"
                                : "underline",
                          }}
                        >
                          {Array.isArray(brandOverviewLastRunData) &&
                          brandOverviewLastRunData?.length > 0
                            ? "See Last 10 Runs"
                            : "No Runs Yet"}
                        </Box>
                      </Typography>
                    )}

                    {/* Manual Config — no destination wired up yet, mirroring
                        the disabled "Manual Configure" quick action in
                        BrandsQuickActions.tsx */}
                    <Typography
                      variant="body2"
                      sx={{
                        ...CARD_CAPTION_SX,
                        color: "rgba(0, 0, 0, 0.38)", // text/disabled
                        display: "inline-flex",
                        alignItems: "center",
                        cursor: "default",
                      }}
                    >
                      <IconButton size="small" disabled sx={{ mr: 0.5, p: 0 }}>
                        <BuildOutlinedIcon fontSize="inherit" />
                      </IconButton>
                      <Box
                        component="span"
                        sx={{ textDecoration: "underline" }}
                      >
                        Manual Config
                      </Box>
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* DailyMonthlyPerformance Tabs */}
            <DailyMonthlyPerformanceTabs />

            {/* Advisor and Unifier Settings (newly added) */}
            <AdvisorAndUnifier />
          </Paper>
          <LastRuns
            open={openLastRunModal}
            handleClose={handleCloseModal}
            tableHeadings={tableHeadings}
          />
        </>
      ) : (
        <>
          <BrandConfigOverviewSkeleton />
        </>
      )}
    </>
  );
};

export default BrandConfigurationOverview;
